// Enhanced CreateBusPage.js with minimal employee API
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, TextField, Button, MenuItem, Paper, Divider,
  Alert, Snackbar, CircularProgress, FormControl, InputLabel, Select,
  Avatar, Skeleton
} from '@mui/material';
import { AccountCircle } from '@mui/icons-material';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { useDispatch, useSelector } from 'react-redux';
import { 
  createBus, 
  clearCreateError,
  selectCreateBusError,
  selectBusFieldErrors,
  selectIsCreatingBus
} from '../slices/busesSlice';
import { fetchStaffMinimal } from '../slices/staffSlice'; // Use minimal API

const MotionPaper = motion(Paper);

const busTypes = ['داخلي', 'خارجي'];

// 🎨 Design System Applied
const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    fontFamily: 'Cairo, Tajawal, sans-serif',
  },
  '& label': { fontFamily: 'Cairo, Tajawal, sans-serif' },
  '& .MuiFormHelperText-root': { fontFamily: 'Cairo, Tajawal, sans-serif' },
};

const CreateBusPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const dispatch = useDispatch();

  // Redux selectors - Use minimal staff data
  const { minimal: staffMinimal, minimalStatus } = useSelector((state) => state.staff);
  const createError = useSelector(selectCreateBusError);
  const fieldErrors = useSelector(selectBusFieldErrors);
  const isCreating = useSelector(selectIsCreatingBus);

  const [bus, setBus] = useState({
    name: '',
    plateNumber: '',
    driver: '',
    type: '',
    capacity: '',
    externalManager: '',
    externalPhone: '',
  });

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [initError, setInitError] = useState(null);

  useEffect(() => {
    // Fetch only drivers with minimal data for better performance
    dispatch(fetchStaffMinimal({ is_archived: false }));
    dispatch(clearCreateError());
  }, [dispatch]);

  // Show snackbar for non-field errors
  useEffect(() => {
    if (createError?.message && !hasFieldErrors()) {
      setShowSnackbar(true);
    }
  }, [createError, fieldErrors]);

  const hasFieldErrors = () => {
    return fieldErrors && Object.keys(fieldErrors).length > 0;
  };

  const getFieldError = (fieldName) => {
    // Map frontend field names to backend field names
    const fieldMapping = {
      name: 'name',
      plateNumber: 'bus_number',
      driver: 'driver',
      type: 'bus_type',
      capacity: 'capacity',
      externalManager: 'manager_name',
      externalPhone: 'phone_number'
    };
    
    const backendFieldName = fieldMapping[fieldName];
    return fieldErrors?.[backendFieldName];
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Prevent negative capacity
    if (name === 'capacity' && (parseInt(value) < 1 || isNaN(value))) {
      return;
    }

    setBus((prev) => ({ ...prev, [name]: value }));
    
    // Clear field-specific error when user starts typing
    if (getFieldError(name)) {
      dispatch(clearCreateError());
    }
    
    // Clear init error when user interacts with form
    if (initError) {
      setInitError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!bus.name.trim()) {
      setInitError('اسم الحافلة مطلوب');
      return;
    }
    
    if (!bus.plateNumber.trim()) {
      setInitError('رقم اللوحة مطلوب');
      return;
    }
    
    if (!bus.type) {
      setInitError('نوع الحافلة مطلوب');
      return;
    }
    
    if (!bus.capacity || parseInt(bus.capacity) < 1) {
      setInitError('السعة مطلوبة ويجب أن تكون أكبر من صفر');
      return;
    }
    
    if (bus.type === 'داخلي' && !bus.driver) {
      setInitError('السائق مطلوب للحافلات الداخلية');
      return;
    }
    
    if (bus.type === 'خارجي') {
      if (!bus.externalManager.trim()) {
        setInitError('اسم المسؤول مطلوب للحافلات الخارجية');
        return;
      }
      if (!bus.externalPhone.trim()) {
        setInitError('رقم الهاتف مطلوب للحافلات الخارجية');
        return;
      }
    }

    // Clear any previous errors
    setInitError(null);
    dispatch(clearCreateError());

    const payload = {
      name: bus.name.trim(),
      bus_number: bus.plateNumber.trim(),
      bus_type: bus.type,
      capacity: parseInt(bus.capacity),
    };

    if (bus.type === 'داخلي') {
      payload.driver = bus.driver;
    } else if (bus.type === 'خارجي') {
      payload.manager_name = bus.externalManager.trim();
      payload.phone_number = bus.externalPhone.trim();
    }

    try {
      await dispatch(createBus(payload)).unwrap();
      showToast('تم إنشاء الحافلة بنجاح!', 'success');
      navigate('/buses');
    } catch (error) {
      console.error('❌ Create bus failed:', error);
      // Error handling is done by Redux
    }
  };

  const handleCancel = () => {
    navigate('/buses');
  };

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
    dispatch(clearCreateError());
  };

  // Available drivers using minimal data - filter for drivers only
  const availableDrivers = staffMinimal.filter(employee => 
    !employee.is_archived && employee.is_driver
  );

  // Get selected driver details for display
  const selectedDriver = availableDrivers.find(driver => driver.id === bus.driver);

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DirectionsBusIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                إضافة حافلة جديدة
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                إدخال بيانات الحافلة وربطها بالسائق المسؤول
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<ArrowForwardIcon />}
            onClick={handleCancel}
            disabled={isCreating}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.6)',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            رجوع للحافلات
          </Button>
        </MotionPaper>

        {/* Form Card */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
        {/* Initial errors */}
        {initError && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 2, fontFamily: 'Cairo, Tajawal, sans-serif' }}
          >
            {initError}
          </Alert>
        )}

        {/* General error alert */}
        {createError?.message && !hasFieldErrors() && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 2, fontFamily: 'Cairo, Tajawal, sans-serif' }}
          >
            {createError.message}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            
            {/* Bus Name */}
            <TextField
              label="اسم الحافلة"
              name="name"
              value={bus.name}
              onChange={handleChange}
              fullWidth
              required
              error={!!getFieldError('name')}
              helperText={getFieldError('name')}
              disabled={isCreating}
              sx={FIELD_SX}
            />

            {/* Plate Number */}
            <TextField
              label="رقم اللوحة"
              name="plateNumber"
              value={bus.plateNumber}
              onChange={handleChange}
              fullWidth
              required
              error={!!getFieldError('plateNumber')}
              helperText={getFieldError('plateNumber')}
              disabled={isCreating}
              sx={FIELD_SX}
            />

            {/* Bus Type */}
            <FormControl
              fullWidth
              required
              error={!!getFieldError('type')}
              disabled={isCreating}
              sx={FIELD_SX}
            >
              <InputLabel>نوع الحافلة</InputLabel>
              <Select
                label="نوع الحافلة"
                name="type"
                value={bus.type}
                onChange={handleChange}
              >
                {busTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              {getFieldError('type') && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, mr: 2 }}>
                  {getFieldError('type')}
                </Typography>
              )}
            </FormControl>

            {/* External Bus Fields */}
            {bus.type === 'خارجي' && (
              <>
                <TextField
                  label="اسم المسؤول"
                  name="externalManager"
                  value={bus.externalManager}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!getFieldError('externalManager')}
                  helperText={getFieldError('externalManager') || ''}
                  disabled={isCreating}
                  sx={FIELD_SX}
                />
                <TextField
                  label="رقم الهاتف"
                  name="externalPhone"
                  value={bus.externalPhone}
                  onChange={handleChange}
                  fullWidth
                  required
                  error={!!getFieldError('externalPhone')}
                  helperText={getFieldError('externalPhone') || ''}
                  disabled={isCreating}
                  sx={FIELD_SX}
                />
              </>
            )}

            {/* Internal Bus Fields - Enhanced Driver Selection */}
            {bus.type === 'داخلي' && (
              <>
                <FormControl
                  fullWidth
                  required
                  error={!!getFieldError('driver')}
                  disabled={isCreating}
                  sx={FIELD_SX}
                >
                  <InputLabel>السائق</InputLabel>
                  <Select
                    label="السائق"
                    name="driver"
                    value={bus.driver}
                    onChange={handleChange}
                  >
                    {minimalStatus === 'loading' ? (
                      <MenuItem disabled>
                        <Stack useFlexGap direction="row" spacing={2} alignItems="center" width="100%">
                          <Skeleton variant="circular" width={32} height={32} />
                          <Skeleton variant="text" width="60%" />
                        </Stack>
                      </MenuItem>
                    ) : (
                      availableDrivers.map((driver) => (
                        <MenuItem key={driver.id} value={driver.id}>
                          <Stack useFlexGap direction="row" spacing={2} alignItems="center">
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: 'primary.main',
                                fontSize: '12px'
                              }}
                            >
                              <AccountCircle />
                            </Avatar>
                            <Box>
                              <Typography variant="body2">
                                {driver.full_name}
                              </Typography>
                              {driver.employee_id && (
                                <Typography variant="caption" color="text.secondary">
                                  رقم الموظف: {driver.employee_id}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  
                  {getFieldError('driver') && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, mr: 2 }}>
                      {getFieldError('driver')}
                    </Typography>
                  )}
                  
                  {availableDrivers.length === 0 && minimalStatus !== 'loading' && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, mr: 2 }}>
                      لا يوجد سائقون متاحون حالياً
                    </Typography>
                  )}
                </FormControl>

                {/* Selected Driver Preview */}
                {selectedDriver && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      bgcolor: '#EEF2FF',
                      border: '1px solid #E5E7EB',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontWeight: 700, color: '#4338CA', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                    >
                      السائق المختار:
                    </Typography>
                    <Stack useFlexGap direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: '#4338CA' }}>
                        <AccountCircle />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                        >
                          {selectedDriver.full_name}
                        </Typography>
                        {selectedDriver.employee_id && (
                          <Typography
                            variant="caption"
                            sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                          >
                            رقم الموظف: {selectedDriver.employee_id}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Paper>
                )}
              </>
            )}

            {/* Capacity (for both types) */}
            {bus.type && (
              <TextField
                label="السعة"
                name="capacity"
                type="number"
                value={bus.capacity}
                onChange={handleChange}
                fullWidth
                required
                error={!!getFieldError('capacity')}
                helperText={getFieldError('capacity')}
                disabled={isCreating}
                inputProps={{ min: 1 }}
                sx={FIELD_SX}
              />
            )}

            <Divider sx={{ my: 1, borderColor: '#E5E7EB' }} />

            {/* Action Buttons */}
            <Stack useFlexGap
              direction={{ xs: 'column-reverse', sm: 'row' }}
              spacing={2}
              justifyContent="flex-end"
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isCreating}
                sx={{
                  minWidth: 120,
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  borderColor: '#DC2626',
                  color: '#DC2626',
                  '&:hover': { borderColor: '#DC2626', bgcolor: '#FEE2E2' },
                }}
              >
                إلغاء
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  isCreating ||
                  (bus.type === 'داخلي' && availableDrivers.length === 0) ||
                  minimalStatus === 'loading'
                }
                sx={{
                  minWidth: 140,
                  borderRadius: 2.5,
                  boxShadow: 'none',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  bgcolor: '#4338CA',
                  color: '#fff',
                  '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                }}
              >
                {isCreating ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                    جاري الحفظ...
                  </>
                ) : (
                  'حفظ الحافلة'
                )}
              </Button>
            </Stack>
          </Stack>
        </form>
        </MotionPaper>

      {/* Snackbar for errors */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: '100%', borderRadius: 2, fontFamily: 'Cairo, Tajawal, sans-serif' }}
        >
          {createError?.message || 'حدث خطأ في إنشاء الحافلة'}
        </Alert>
      </Snackbar>
      </Box>
    </Box>
  );
};

export default CreateBusPage;